export const PROMOTION_FILTERS = [
  { value: "all", labelKey: "all_promotions" },
  { value: "bill_discount", labelKey: "bill_discount" },
  { value: "item_discount", labelKey: "item_discount" },
  { value: "category_discount", labelKey: "category_discount" },
  { value: "buyx_gety", labelKey: "buyx_gety" },
];

export const PROMOTION_STATUS_FILTERS = [
  { value: "active", labelKey: "active" },
  { value: "inactive", labelKey: "inactive" },
];

export const PROMOTION_TYPE_OPTIONS = [
  "bill_discount",
  "item_discount",
  "category_discount",
  "buyx_gety",
];

export const TARGET_TYPE_BY_PROMOTION_TYPE = {
  bill_discount: "all",
  item_discount: "product",
  category_discount: "category",
  buyx_gety: "product",
};

export const ACTIVE_DAY_OPTIONS = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

export function targetTypeForPromotionType(promotionType) {
  return TARGET_TYPE_BY_PROMOTION_TYPE[promotionType] || "all";
}

export function createPromotionForm() {
  return {
    code: "",
    name: "",
    promotion_type: "bill_discount",
    points_required: 0,
    usage_limit: null,
    target_type: "all",
    discount_type: "percent",
    used: "config",
    discount_value: null,
    note: "",
    start_date: "",
    end_date: "",
    active_days: [],
    start_time: "",
    end_time: "",
    is_active: 1,
    imageFile: null,
    imagePath: null,
    category_ids: [],
    product_ids: [],
    buy_items: [],
    get_items: [],
  };
}

export function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export function normalizeIdArray(values) {
  if (!Array.isArray(values)) return [];
  return values
    .map((value) => {
      if (value && typeof value === "object") {
        return Number(value.id);
      }
      return Number(value);
    })
    .filter((value) => Number.isFinite(value) && value > 0);
}

export function getEntityLabel(item) {
  if (!item) return "";
  return (
    item.label ||
    item.name ||
    item.la_name ||
    item.en_name ||
    item.th_name ||
    item.cn_name ||
    item.code ||
    `#${item.id}`
  );
}

export function normalizeCategoryItem(item) {
  return {
    id: Number(item.id),
    label: getEntityLabel(item),
    raw: item,
  };
}

export function getProductLabel(item, locale = "en") {
  if (!item) return "";
  // Products only carry name/name_en/name_th/name_cn — "name" itself is the
  // Lao name, there is no separate la_name/name_la field like on categories.
  const byLocale = {
    la: [item.name],
    th: [item.name_th, item.th_name],
    cn: [item.name_cn, item.cn_name],
    ch: [item.name_cn, item.cn_name],
  }[locale] || [item.name_en, item.en_name];

  return (
    byLocale.find(Boolean) ||
    item.name ||
    item.name_en ||
    item.en_name ||
    item.code ||
    `#${item.id}`
  );
}

export function normalizeVariantItem(variant) {
  const options = Array.isArray(variant?.options) ? variant.options : [];
  const optionLabel = options
    .map((option) => option.value)
    .filter(Boolean)
    .join(" / ");
  return {
    id: Number(variant.id),
    label: variant.label || optionLabel || variant.sku || `#${variant.id}`,
    sku: variant.sku || "",
    barcode: variant.barcode || "",
    price: variant.price ?? null,
    stock: variant.stock ?? null,
    options,
  };
}

export function normalizeProductItem(item, locale = "en") {
  const category =
    item.category ||
    item.category_id ||
    item.product_category ||
    item.category_detail ||
    null;
  return {
    id: Number(item.id),
    label: getProductLabel(item, locale),
    categoryLabel: getEntityLabel(category) || "",
    image: item.img || item.image || null,
    code: item.code || item.barcode || "",
    priceSale: item.price_sale ?? item.priceSale ?? null,
    currencyCode: item.currency?.code || item.currency_code || "LAK",
    hasVariations: Number(item.has_variations) === 1,
    variants: Array.isArray(item.variants) ? item.variants.map(normalizeVariantItem) : [],
    raw: item,
  };
}

export function mapPromotionToForm(promotion, locale = "en") {
  const form = createPromotionForm();
  const categoryItems = [];
  const productItems = [];
  const categoryIds = [];
  const productIds = [];
  const buyItems = [];
  const getItems = [];
  const isBuyGet = promotion?.promotion_type === "buyx_gety";

  (promotion?.participants || []).forEach((participant) => {
    const participantVariants =
      participant.participant_variants || participant.participantVariants || [];
    const variantIds = normalizeIdArray(
      participantVariants.map((v) => v.product_variant_id)
    );
    const variantLabels = participantVariants
      .map((v) => v.product_variant || v.productVariant)
      .filter(Boolean)
      .map((variant) => normalizeVariantItem(variant).label);

    if (isBuyGet) {
      if (!participant.product_id || !participant.product) return;
      const item = {
        product_id: Number(participant.product_id),
        qty: Number(participant.qty || 1),
        label: getProductLabel(participant.product, locale),
        variant_ids: variantIds,
        variantLabels,
      };
      if (participant.role === "buy") {
        buyItems.push(item);
      } else {
        getItems.push(item);
      }
      return;
    }
    if (participant.category_id && participant.category) {
      categoryIds.push(Number(participant.category_id));
      categoryItems.push(normalizeCategoryItem(participant.category));
    }
    if (participant.product_id && participant.product) {
      productIds.push(Number(participant.product_id));
      const productItem = normalizeProductItem(participant.product, locale);
      productItem.variantIds = variantIds;
      productItems.push(productItem);
    }
  });

  form.code = promotion?.code || "";
  form.name = promotion?.name || "";
  form.promotion_type = promotion?.promotion_type || "bill_discount";
  form.points_required =
    promotion?.points_required === null || promotion?.points_required === undefined
      ? null
      : Number(promotion.points_required);
  form.usage_limit =
    promotion?.usage_limit === null || promotion?.usage_limit === undefined
      ? null
      : Number(promotion.usage_limit);
  form.target_type =
    promotion?.target_type || targetTypeForPromotionType(form.promotion_type);
  form.discount_type = promotion?.discount_type || "percent";
  form.used = promotion?.used || "config";
  form.discount_value =
    promotion?.discount_value === null || promotion?.discount_value === undefined
      ? null
      : Number(promotion.discount_value);
  form.note = promotion?.note || "";
  form.start_date = formatDateForInput(promotion?.start_date);
  form.end_date = formatDateForInput(promotion?.end_date);
  form.active_days = Array.isArray(promotion?.active_days)
    ? promotion.active_days
    : [];
  form.start_time = promotion?.start_time ? String(promotion.start_time).slice(0, 5) : "";
  form.end_time = promotion?.end_time ? String(promotion.end_time).slice(0, 5) : "";
  form.is_active = Number(promotion?.is_active ?? 1);
  form.imagePath = promotion?.image || null;
  form.category_ids = normalizeIdArray(categoryIds);
  form.product_ids = normalizeIdArray(productIds);
  form.buy_items = buyItems;
  form.get_items = getItems;

  return {
    form,
    categoryItems: dedupeItems(categoryItems),
    productItems: dedupeItems(productItems),
  };
}

export function buildPromotionPayload(form, productItems = []) {
  const targetType = targetTypeForPromotionType(form.promotion_type);
  const payload = {
    code: form.code?.trim() || "",
    name: form.name?.trim() || "",
    promotion_type: form.promotion_type,
    points_required:
      form.points_required === null || form.points_required === ""
        ? null
        : Number(form.points_required),
    usage_limit:
      form.usage_limit === null || form.usage_limit === ""
        ? null
        : Number(form.usage_limit),
    target_type: targetType,
    discount_type: null,
    used: form.used || "config",
    discount_value: null,
    note: form.note || "",
    start_date: toApiDate(form.start_date, false),
    end_date: toApiDate(form.end_date, true),
    active_days: Array.isArray(form.active_days) ? form.active_days : [],
    start_time: form.start_time || null,
    end_time: form.end_time || null,
    is_active: form.is_active ? 1 : 0,
    category_ids: [],
    product_ids: [],
    product_variants: [],
    buy_items: [],
    get_items: [],
  };

  if (form.promotion_type === "buyx_gety") {
    payload.buy_items = normalizeItemArray(form.buy_items);
    payload.get_items = normalizeItemArray(form.get_items);

    return payload;
  }

  payload.discount_type = form.discount_type || "percent";
  payload.discount_value =
    form.discount_value === null || form.discount_value === ""
      ? 0
      : Number(form.discount_value);

  if (targetType === "category") {
    payload.category_ids = normalizeIdArray(form.category_ids);
  }

  if (targetType === "product") {
    payload.product_ids = normalizeIdArray(form.product_ids);
    payload.product_variants = (productItems || [])
      .filter((item) => Array.isArray(item.variantIds) && item.variantIds.length)
      .map((item) => ({
        product_id: Number(item.id),
        variant_ids: normalizeIdArray(item.variantIds),
      }));
  }

  return payload;
}

export function normalizeItemArray(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      product_id: Number(item?.product_id),
      qty: Math.max(1, Number(item?.qty) || 1),
      variant_ids: normalizeIdArray(item?.variant_ids || []),
    }))
    .filter((item) => Number.isFinite(item.product_id) && item.product_id > 0);
}

export function buildPromotionFormData(form, productItems = []) {
  const payload = buildPromotionPayload(form, productItems);
  const formData = new FormData();

  Object.keys(payload).forEach((key) => {
    const value = payload[key];
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item && typeof item === "object") {
          Object.keys(item).forEach((itemKey) => {
            const itemValue = item[itemKey];
            if (Array.isArray(itemValue)) {
              itemValue.forEach((nested) => {
                formData.append(`${key}[${index}][${itemKey}][]`, nested);
              });
              return;
            }
            formData.append(`${key}[${index}][${itemKey}]`, itemValue);
          });
          return;
        }
        formData.append(`${key}[]`, item);
      });
      return;
    }

    if (value === null || value === undefined || value === "") {
      return;
    }

    formData.append(key, value);
  });

  if (form.imageFile) {
    formData.append("file", form.imageFile);
  }

  return formData;
}

export function formatDateForInput(value) {
  if (!value) return "";

  // The API serializes start_date/end_date as UTC ("...T17:00:00.000000Z")
  // while the store's local day is what the form should show — e.g. local
  // midnight 2026-09-02 in Asia/Bangkok (UTC+7) becomes 2026-09-01T17:00:00Z.
  // Slicing that UTC string directly (or using toISOString) reads back the
  // wrong (previous) calendar day, so parse it and read LOCAL date parts
  // instead, which re-applies the browser's timezone offset correctly.
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatPromotionDateRange(startDate, endDate) {
  const start = formatReadableDate(startDate);
  const end = formatReadableDate(endDate);

  if (start && end) return `${start} - ${end}`;
  return start || end || "";
}

export function formatPromotionTimeRange(startTime, endTime) {
  const start = startTime ? String(startTime).slice(0, 5) : "";
  const end = endTime ? String(endTime).slice(0, 5) : "";

  if (start && end) return `${start} - ${end}`;
  return start || end || "";
}

function toApiDate(value, endOfDay) {
  if (!value) return null;
  return `${value}T${endOfDay ? "23:59:59" : "00:00:00"}`;
}

function formatReadableDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function dedupeItems(items) {
  const map = new Map();
  items.forEach((item) => {
    if (item?.id) {
      map.set(Number(item.id), item);
    }
  });
  return Array.from(map.values());
}
